import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useForm, FormProvider } from 'react-hook-form';
import { format, isBefore } from 'date-fns';

import { helper } from '@myrealtrip/frontend-weblog';
import { media } from '@myrealtrip/modash';
import {
  IcoCalendarStart,
  IcoCompareArrowsVertical,
  IcoError,
  IcoLocationOn,
  IcoPerson,
} from '@myrealtrip/web-icons';
import {
  ButtonGeneral,
  InputText,
  Text,
  UnstyledButton,
  useIsomorphicLayoutEffect,
} from '@myrealtrip/web-ui';

import Show from '~components/Show';
import SwitchCase from '~components/SwitchCase';
import { STORAGE_ITEMS } from '~constants/common';
import {
  DEFAULT_DEPARTURE_TIME,
  JOURNEY_TYPE,
  RAIL_EUROPE_SEARCH_CONDITION_TYPE,
} from '~constants/pointToPointTicket';
import { WeblogEventName, WeblogPageCategory, WeblogScreenName } from '~constants/weblog';
import useToast from '~hooks/useToast';
import { useDetermineMobile } from '@myrealtrip/device-detect';
import useLocalStorage from '~hooks/useLocalStorage';
import { useMounted } from '~hooks/useMounted';
import useErrorFeedback from '~hooks/pointToPointTicket/useErrorFeedback';
import useRailEuropeNavigation from '~hooks/pointToPointTicket/useRailEuropeNavigation';
import useOutboundSearch from '~queries/transportation/pointToPointTicket/useOutboundSearch';
import parseRailEuropeQueryParams from '~utils/pointToPointTicket/parseRailEuropeQueryParams';
import { omit } from '~utils/omit';
import { formatDate, formatDateTimeRange } from '~utils/formatDate';
import {
  mapToNavigationParams,
  mapToSearchRequest,
  mapToStorageData,
} from '~utils/pointToPointTicket/searchResultMappers';
import {
  isInvalidPlaceCondition,
  validateBeforeSubmit,
} from '~utils/pointToPointTicket/searchConditionsValidators';
import { DATE_REGEX, toLocalDate } from '~utils/toLocalDate';

import type {
  PointToPointTicketFormProps,
  SearchConditionStorageValues,
} from '~types/pointToPointTicket';
import type {
  JourneyType,
  OptionalRailEuropeTrainQueryParam,
  SearchConditionType,
} from '~types/queryParam';

import JourneyChips from './SearchConditions/SearchType/JourneyChips';
import SearchConditionContent from './SearchConditions/SearchConditionContent';
import SearchLoading from './SearchConditions/SearchLoading';

const { sendClickLog } = helper;

interface Props {
  selectedJourney: JourneyType;
  onChangeJourney: (journey: JourneyType) => void;
}

const START_POSITION = 16;
const TOP_POSITION = 48;

const SearchConditions = ({ selectedJourney, onChangeJourney }: Props) => {
  const { query } = useRouter();
  const isMounted = useMounted();

  const isMobile = useDetermineMobile();
  const theme = useTheme();
  const toast = useToast();

  const { errorDialog, handleErrorFeedback } = useErrorFeedback();

  const { mutateAsync: searchOutbound, isPending } = useOutboundSearch();

  const { navigateToSearchResult } = useRailEuropeNavigation();
  const [savedSearchCondition, setSavedSearchCondition] =
    useLocalStorage<SearchConditionStorageValues>(
      STORAGE_ITEMS.railEuropeSearch(selectedJourney.toLowerCase()),
    );

  const isSingleTrip = selectedJourney === JOURNEY_TYPE.ONE_WAY;

  const [openContent, setOpenContent] = useState<SearchConditionType | null>(null);

  const closeContent = () => {
    setOpenContent(null);
  };

  const getInitialValues = (): Partial<PointToPointTicketFormProps> => {
    const defaultOutboundDepartureTime = DEFAULT_DEPARTURE_TIME;
    const defaultInboundDepartureTime = isSingleTrip ? undefined : DEFAULT_DEPARTURE_TIME;

    // URL 쿼리 파라미터가 있는 경우 이를 우선적으로 사용
    if (Object.keys(query).length > 0) {
      const {
        origin,
        destination,
        departureDate,
        arrivalDate,
        ages,
        outboundDepartureTime,
        inboundDepartureTime,
      } = query;

      // 쿼리 파라미터에 필수 값들이 있는 경우
      if (
        origin ||
        destination ||
        departureDate ||
        ages ||
        outboundDepartureTime ||
        inboundDepartureTime
      ) {
        const parsedQuery = parseRailEuropeQueryParams(query as OptionalRailEuropeTrainQueryParam);

        return {
          origin:
            parsedQuery.origin.place && parsedQuery.origin.type && parsedQuery.origin.code
              ? {
                  code: parsedQuery.origin.code,
                  type: parsedQuery.origin.type,
                  label: parsedQuery.origin.place,
                }
              : undefined,
          destination:
            parsedQuery.destination.place &&
            parsedQuery.destination.type &&
            parsedQuery.destination.code
              ? {
                  code: parsedQuery.destination.code,
                  type: parsedQuery.destination.type,
                  label: parsedQuery.destination.place,
                }
              : undefined,
          departureDate: (departureDate as string) || '',
          arrivalDate: isSingleTrip ? undefined : (arrivalDate as string) || '',
          outboundDepartureTime: parsedQuery.outboundDepartureTime || defaultOutboundDepartureTime,
          inboundDepartureTime: parsedQuery.inboundDepartureTime || defaultInboundDepartureTime,
          ages: ages ? (ages as string).split(',').map((age) => parseInt(age)) : [],
        };
      }
    }

    const defaultDepartureDate = isSingleTrip ? format(new Date(), 'yyyy-MM-dd') : '';
    const defaultArrivalDate = isSingleTrip ? undefined : '';

    // localStorage에 저장된 값이 있는 경우
    if (Object.values(savedSearchCondition).length > 0 && savedSearchCondition.departureDate) {
      const savedDepartureDate = new Date(toLocalDate(savedSearchCondition.departureDate));
      const omittedCondition = {
        ...omit<SearchConditionStorageValues, 'journey'>(savedSearchCondition, ['journey']),
        ...(savedSearchCondition.origin && {
          origin: {
            ...savedSearchCondition.origin,
            label: savedSearchCondition.origin.label,
          },
        }),
        ...(savedSearchCondition.destination && {
          destination: {
            ...savedSearchCondition.destination,
            label: savedSearchCondition.destination.label,
          },
        }),
        outboundDepartureTime:
          savedSearchCondition.outboundDepartureTime || defaultOutboundDepartureTime,
        inboundDepartureTime:
          savedSearchCondition.inboundDepartureTime || defaultInboundDepartureTime,
      } as Partial<PointToPointTicketFormProps>;

      if (isBefore(savedDepartureDate, new Date())) {
        return {
          ...omittedCondition,
          departureDate: defaultDepartureDate,
          arrivalDate: defaultArrivalDate,
        };
      }

      return omittedCondition;
    }

    return {
      departureDate: defaultDepartureDate,
      arrivalDate: defaultArrivalDate,
      outboundDepartureTime: defaultOutboundDepartureTime,
      inboundDepartureTime: defaultInboundDepartureTime,
      ages: [],
    };
  };

  const methods = useForm<PointToPointTicketFormProps>({
    defaultValues: getInitialValues(),
  });

  const departureDate = methods.watch('departureDate');
  const arrivalDate = methods.watch('arrivalDate');
  const outboundDepartureTime = methods.watch('outboundDepartureTime');
  const inboundDepartureTime = methods.watch('inboundDepartureTime');

  const [hasOutboundDateTimeValue, setHasOutboundDateTimeValue] = useState(false);

  useIsomorphicLayoutEffect(() => {
    setHasOutboundDateTimeValue(!!getDateTimeValue(departureDate, outboundDepartureTime));
  }, [departureDate, outboundDepartureTime]);

  const hasDepartureTimeCondition = isSingleTrip
    ? !!outboundDepartureTime
    : !!outboundDepartureTime && !!inboundDepartureTime;

  const isValidDateString = (date: string) => {
    return DATE_REGEX.test(date);
  };

  const getDateValue = () => {
    const startDateTime = methods.getValues('departureDate');
    const validStartDateTime =
      startDateTime && isValidDateString(startDateTime) ? startDateTime : undefined;
    const endDateTime = methods.getValues('arrivalDate');
    const validEndDateTime =
      endDateTime && isValidDateString(endDateTime) ? endDateTime : undefined;

    if (selectedJourney === JOURNEY_TYPE.ROUND_TRIP && validStartDateTime && validEndDateTime) {
      return formatDateTimeRange('MM월 dd일(E)', validStartDateTime, validEndDateTime);
    }

    if (validStartDateTime) {
      return formatDate(validStartDateTime, { formatStr: 'MM월 dd일(E)' });
    }

    return '';
  };

  const getDateTimeValue = (date?: string, time?: string) => {
    if (!date || !isValidDateString(date)) return '';
    return `${formatDate(date, { formatStr: 'MM월 dd일(E)' })}${
      time ? ` ${time.slice(0, 2)}시~` : ''
    }`;
  };

  const handleChangeJourney = (changingJourney: JourneyType) => {
    onChangeJourney(changingJourney);

    const isSingleTrip = changingJourney === JOURNEY_TYPE.ONE_WAY;
    const currentDepartureDate = methods.getValues('departureDate');
    const defaultDepartureDate = isSingleTrip ? format(new Date(), 'yyyy-MM-dd') : '';
    const departureDate =
      currentDepartureDate || savedSearchCondition.departureDate || defaultDepartureDate;

    methods.reset({
      ...methods.getValues(),
      ...(isSingleTrip
        ? {
            departureDate,
            arrivalDate: undefined,
            outboundDepartureTime: DEFAULT_DEPARTURE_TIME,
            inboundDepartureTime: undefined,
          }
        : {
            departureDate: '',
            arrivalDate: '',
            outboundDepartureTime: DEFAULT_DEPARTURE_TIME,
            inboundDepartureTime: DEFAULT_DEPARTURE_TIME,
          }),
    });
  };

  const handleSwitchPlace = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const { origin: currentOrigin, destination: currentDestination } = methods.getValues();
    methods.reset({
      ...methods.getValues(),
      origin: currentDestination,
      destination: currentOrigin,
    });
  };

  const onSubmit = async (data: PointToPointTicketFormProps) => {
    if (isInvalidPlaceCondition(data)) {
      toast.error({
        id: 'different-origin-destination',
        message: '목적지는 출발지와 다른 지역이어야 합니다.',
      });
      return;
    }

    // 필수 값이 없으면 제출하지 않음
    const { id, error, validationMessage } = validateBeforeSubmit({
      data,
      isSingleTrip,
    });
    if (error) {
      toast.warning({ id, message: validationMessage, icon: <IcoError size={24} /> });
      return;
    }

    toast.clear();

    const searchParams = mapToSearchRequest({
      journeyType: selectedJourney,
      origin: {
        code: data.origin.code,
        type: data.origin.type,
        label: data.origin.label,
      },
      destination: {
        code: data.destination.code,
        type: data.destination.type,
        label: data.destination.label,
      },
      departureDate: data.departureDate,
      arrivalDate: data.arrivalDate,
      outboundDepartureTime: data.outboundDepartureTime,
      inboundDepartureTime: data.inboundDepartureTime,
      ages: data.ages,
    });

    try {
      const { data: result } = await searchOutbound(searchParams);

      sendClickLog({
        pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
        screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
        eventName: WeblogEventName.SEARCH,
        itemKind: 'button',
        itemName: '검색',
        departure: data.origin.label,
        arrival: data.destination.label,
        departureDate: data.departureDate,
        ...(data.arrivalDate && { arrivalDate: data.arrivalDate }),
        ...(data.outboundDepartureTime && { outboundDepartureTime: data.outboundDepartureTime }),
        ...(data.inboundDepartureTime && { inboundDepartureTime: data.inboundDepartureTime }),
        pax: data.ages.length,
        journey: selectedJourney === JOURNEY_TYPE.ONE_WAY ? 'OW' : 'RT',
      });

      toast.clear();
      setSavedSearchCondition(
        mapToStorageData({
          request: result.request,
          correlationId: result.correlationId,
          id: result.id,
        }),
      );
      navigateToSearchResult(mapToNavigationParams(result));
    } catch (error) {
      handleErrorFeedback(error, { id: 'search-error' });
    }
  };

  const handleOpenContent = (contentType: SearchConditionType) => () => {
    if (isPending) return;
    if (isMobile) {
      toast.clear();
    }
    setOpenContent(contentType);
  };

  useEffect(() => {
    if (
      query.journey &&
      (query.journey === JOURNEY_TYPE.ONE_WAY || query.journey === JOURNEY_TYPE.ROUND_TRIP) &&
      query.journey !== selectedJourney
    ) {
      onChangeJourney(query.journey as JourneyType);
    }
  }, []);

  return (
    <Container>
      {isPending && <SearchLoading isPending={isPending} />}
      <JourneyChips selectedJourney={selectedJourney} onChangeJourney={handleChangeJourney} />
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <InputContainer>
            <PlaceSearchWrapper>
              <InputWrapper
                disabled={isPending}
                onClick={handleOpenContent(RAIL_EUROPE_SEARCH_CONDITION_TYPE.ORIGIN)}
              >
                <StyledPlaceInputText
                  readOnly
                  isFirst
                  leftIcon={<IcoLocationOn size={20} color={theme.colors.gray.s700} />}
                  placeholder="출발지"
                  value={methods.getValues('origin')?.label || ''}
                />
              </InputWrapper>
              <InputWrapper
                disabled={isPending}
                onClick={handleOpenContent(RAIL_EUROPE_SEARCH_CONDITION_TYPE.DESTINATION)}
              >
                <StyledPlaceInputText
                  isLast
                  leftIcon={<IcoLocationOn size={20} color={theme.colors.gray.s700} />}
                  placeholder="도착지"
                  value={methods.getValues('destination')?.label || ''}
                />
              </InputWrapper>
              <SwitchPlaceButton onClick={handleSwitchPlace}>
                <IcoCompareArrowsVertical size={16} />
              </SwitchPlaceButton>
            </PlaceSearchWrapper>
            <InputWrapper
              disabled={isPending}
              onClick={handleOpenContent(RAIL_EUROPE_SEARCH_CONDITION_TYPE.DATE)}
            >
              <SwitchCase
                value={hasDepartureTimeCondition.toString()}
                caseBy={{
                  ['true']: (
                    <>
                      <StyledDateTimeInputText
                        readOnly
                        leftIcon={<IcoCalendarStart size={20} color={theme.colors.gray.s700} />}
                        placeholder="일정"
                        value={hasOutboundDateTimeValue ? ' ' : ''}
                        displayWidenHeight={!isSingleTrip && hasOutboundDateTimeValue}
                      />
                      <Show when={isMounted && hasOutboundDateTimeValue}>
                        <CustomDateTimeValue>
                          {isSingleTrip ? (
                            <span>{getDateTimeValue(departureDate, outboundDepartureTime)}</span>
                          ) : (
                            <DateTimeWrapper displayWidenHeight={!isSingleTrip}>
                              <span>{getDateTimeValue(departureDate, outboundDepartureTime)}</span>
                              <DateTimeDivider />
                              <span>{getDateTimeValue(arrivalDate, inboundDepartureTime)}</span>
                            </DateTimeWrapper>
                          )}
                        </CustomDateTimeValue>
                      </Show>
                    </>
                  ),
                }}
                defaultComponent={
                  <StyledDateTimeInputText
                    readOnly
                    leftIcon={<IcoCalendarStart size={20} color={theme.colors.gray.s700} />}
                    placeholder="일정"
                    value={getDateValue() || ''}
                  />
                }
              />
            </InputWrapper>
            <InputWrapper
              disabled={isPending}
              onClick={handleOpenContent(RAIL_EUROPE_SEARCH_CONDITION_TYPE.AGES)}
            >
              <StyledInputText
                readOnly
                leftIcon={<IcoPerson size={20} color={theme.colors.gray.s700} />}
                placeholder="탑승 인원"
                value={
                  methods.getValues('ages')?.length > 0
                    ? `${methods.getValues('ages').length}명`
                    : ''
                }
              />
            </InputWrapper>
            <SearchConditionContent
              // FIXME : 각 팝업을 분리해서 마개조한 스타일 제거 필요
              popupLocations={[
                {
                  type: RAIL_EUROPE_SEARCH_CONDITION_TYPE.ORIGIN,
                  css: {
                    maxHeight: 496,
                    top: TOP_POSITION,
                    left: START_POSITION,
                  },
                },
                {
                  type: RAIL_EUROPE_SEARCH_CONDITION_TYPE.DESTINATION,
                  css: {
                    maxHeight: 496,
                    top: TOP_POSITION * 2,
                    left: START_POSITION,
                  },
                },
                {
                  type: RAIL_EUROPE_SEARCH_CONDITION_TYPE.DATE,
                  css: {
                    maxHeight: 560,
                    top: TOP_POSITION * 3 + 6,
                    left: START_POSITION,
                  },
                },
                {
                  type: RAIL_EUROPE_SEARCH_CONDITION_TYPE.AGES,
                  css: {
                    top: TOP_POSITION * 4 + 6 * 2,
                    left: START_POSITION,
                  },
                },
              ]}
              selectedJourney={selectedJourney}
              openContent={openContent}
              closeContent={closeContent}
              showTimeOption
            />
          </InputContainer>

          <ButtonGeneral
            type="submit"
            isFullWidth
            shape="rectangle"
            disabled={isPending}
            isLoading={isPending}
          >
            <Text typography="headlineNormal15">검색</Text>
          </ButtonGeneral>
        </Form>
      </FormProvider>
      {errorDialog}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 20px;
  margin-top: 12px;

  ${media.minMobile} {
    margin-top: 8px;
    padding: 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PlaceSearchWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InputWrapper = styled.div<{ disabled: boolean }>`
  position: relative;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
    `}
`;

const StyledInputText = styled(InputText)`
  > span {
    background: ${({ theme }) => theme.colors.gray.s80};
    border-radius: 16px;
    border: none;
    box-shadow: none;
    height: 48px;
  }
  input {
    background: ${({ theme }) => theme.colors.gray.s80};
    color: ${({ theme }) => theme.colors.gray.s1000};
    pointer-events: none;

    ${({ theme }) => theme.typography.paragraphNormal15};

    &::placeholder {
      color: ${({ theme }) => theme.colors.gray.s400};
    }
  }
`;

const StyledPlaceInputText = styled(StyledInputText)<{ isFirst?: boolean; isLast?: boolean }>`
  ${({ isFirst }) =>
    isFirst &&
    css`
      > span:first-of-type {
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    `}

  ${({ isLast }) =>
    isLast &&
    css`
      > span:last-of-type {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        border-bottom-left-radius: 16px;
        border-bottom-right-radius: 16px;
      }
    `}
`;

const SwitchPlaceButton = styled(UnstyledButton)`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  background: ${({ theme }) => theme.colors.white};
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const lessThan375 = media.custom(375, 'max');

const StyledDateTimeInputText = styled(StyledInputText)<{ displayWidenHeight?: boolean }>`
  > span {
    ${lessThan375} {
      ${({ displayWidenHeight }) =>
        displayWidenHeight &&
        css`
          height: 71px;
        `}
    }
  }
`;

const CustomDateTimeValue = styled.div`
  position: absolute;
  left: 40px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.paragraphNormal15};
  color: ${({ theme }) => theme.colors.gray.s1000};
  word-break: keep-all;
  pointer-events: none;
  padding-right: 14px;

  ${lessThan375} {
    padding-block: 13.5px;
  }
`;

const DateTimeWrapper = styled.div<{ displayWidenHeight: boolean }>`
  display: flex;
  gap: 8px;
  align-items: center;

  ${lessThan375} {
    ${({ displayWidenHeight }) =>
      displayWidenHeight &&
      css`
        flex-direction: column;
        gap: 2px;
      `}
  }
`;

const DateTimeDivider = styled.div`
  width: 1.5px;
  height: 16px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.gray.s300};

  ${lessThan375} {
    display: none;
  }
`;

export default SearchConditions;
